import './Button.scss';
import cn from 'classnames';
import {
  ButtonHTMLAttributes,
  MouseEventHandler,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  Ref,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  Appearance,
  AppearanceType,
  Background,
  BackgroundType,
  Design,
  DesignType,
  Dimensions,
  Intent,
  IntentType,
  SizeType,
  Placement,
  PlacementType,
  Size
} from '../designConstants';
import { Spinner } from '../Spinner';
import { useToolbarSize } from '../Toolbar';

// Matches the button's content height per size in Button.scss so the loading
// spinner doesn't push the button taller than its text would.
const SPINNER_SIZE_BY_BUTTON_SIZE: Record<SizeType, number> = {
  [Size.SM]: 16,
  [Size.MD]: 20,
  [Size.LG]: 24
};

export type ButtonDesignType = DesignType | Exclude<IntentType, typeof Intent.INFO>;

interface ButtonTooltip {
  text: string;
  placement?: PlacementType;
  class?: string;
}

interface ButtonProps
  extends PropsWithChildren, Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  type?: 'button' | 'submit';
  ref?: Ref<HTMLButtonElement>;
  appearance?: AppearanceType;
  size?: SizeType;
  design?: ButtonDesignType;
  background?: BackgroundType;
  className?: string;
  leadingElement?: ReactNode;
  leadingElementIsInteractive?: boolean;
  trailingElement?: ReactNode;
  trailingElementIsInteractive?: boolean;
  disabled?: boolean;
  loading?: boolean;
  disableAnimation?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  tooltip?: ButtonTooltip;
}

export const bpmBtnClassName = ({
  appearance,
  size,
  design,
  background,
  className
}: {
  appearance?: AppearanceType;
  size?: SizeType;
  design?: ButtonDesignType;
  background?: BackgroundType;
  className?: string;
}): string => cn('bpm-btn', appearance, size, design, background, className);

export const Button = ({
  children,
  ref,
  appearance = Appearance.FILLED,
  size: sizeProp,
  design = Design.BRAND,
  background = Background.LIGHT,
  className,
  leadingElement: leadingElementProp,
  leadingElementIsInteractive,
  trailingElement: trailingElementProp,
  trailingElementIsInteractive,
  disabled = false,
  loading = false,
  disableAnimation = false,
  onClick = () => {},
  tooltip,
  type = 'button',
  ...buttonPassthroughProps
}: ButtonProps) => {
  const size = useToolbarSize(sizeProp);
  const [animateOnClick, setAnimateOnClick] = useState(false);
  const leadingInteractiveElRef = useRef<HTMLDivElement>(null);
  const [leadingInteractiveElDims, setLeadingInteractiveElDims] = useState<Dimensions | null>(null);
  const trailingInteractiveElRef = useRef<HTMLDivElement>(null);
  const [trailingInteractiveElDims, setTrailingInteractiveElDims] = useState<Dimensions | null>(
    null
  );

  const leadingElement: ReactNode = useMemo(() => {
    if (!leadingElementProp) {
      return null;
    }

    if (leadingElementIsInteractive) {
      return (
        <div
          className='hidden-spacer'
          style={{
            width: `${leadingInteractiveElDims?.width}px`,
            height: `${leadingInteractiveElDims?.height}px`
          }}
        />
      );
    }

    return <div className='bpm-btn-icon leading'>{leadingElementProp}</div>;
  }, [
    leadingElementIsInteractive,
    leadingElementProp,
    leadingInteractiveElDims?.height,
    leadingInteractiveElDims?.width
  ]);

  const interactiveLeadingElement = useMemo(() => {
    if (!leadingElementProp || !leadingElementIsInteractive) {
      return null;
    }

    return (
      <div className={cn('interactive-container leading', size)} ref={leadingInteractiveElRef}>
        {leadingElementProp}
      </div>
    );
  }, [leadingElementIsInteractive, leadingElementProp, size]);

  const trailingElement: ReactNode = useMemo(() => {
    if (!trailingElementProp) {
      return null;
    }

    if (trailingElementIsInteractive) {
      return (
        <div
          className='hidden-spacer'
          style={{
            width: `${trailingInteractiveElDims?.width}px`,
            height: `${trailingInteractiveElDims?.height}px`
          }}
        />
      );
    }

    return <div className='bpm-btn-icon trailing'>{trailingElementProp}</div>;
  }, [
    trailingElementIsInteractive,
    trailingElementProp,
    trailingInteractiveElDims?.height,
    trailingInteractiveElDims?.width
  ]);

  const interactiveTrailingElement = useMemo(() => {
    if (!trailingElementProp || !trailingElementIsInteractive) {
      return null;
    }

    return (
      <div className={cn('interactive-container trailing', size)} ref={trailingInteractiveElRef}>
        {trailingElementProp}
      </div>
    );
  }, [trailingElementProp, trailingElementIsInteractive, size]);

  useLayoutEffect(() => {
    if (!leadingInteractiveElRef.current) return;

    const { children: leadingInteractiveChildren } = leadingInteractiveElRef.current;
    if (leadingInteractiveChildren.length !== 1) return;

    const { width, height } = leadingInteractiveChildren[0].getBoundingClientRect();
    setLeadingInteractiveElDims({ width, height });
  }, [leadingElement]);

  useLayoutEffect(() => {
    if (!trailingInteractiveElRef.current) return;

    const { children: trailingInteractiveChildren } = trailingInteractiveElRef.current;
    if (trailingInteractiveChildren.length !== 1) return;

    const { width, height } = trailingInteractiveChildren[0].getBoundingClientRect();
    setTrailingInteractiveElDims({ width, height });
  }, [trailingElement]);

  return (
    <>
      {interactiveLeadingElement}
      <TooltipWrapper tooltip={tooltip}>
        <button
          {...buttonPassthroughProps}
          className={cn(bpmBtnClassName({ appearance, size, design, background, className }), {
            animated: animateOnClick,
            'has-interactive-leading': leadingElement && leadingElementIsInteractive,
            'has-interactive-trailing': trailingElement && trailingElementIsInteractive
          })}
          type={type === 'submit' ? 'submit' : 'button'}
          ref={ref}
          onClick={e => {
            onClick(e);

            if (!disableAnimation) {
              setAnimateOnClick(true);
            }
          }}
          onAnimationEnd={() => setAnimateOnClick(false)}
          disabled={disabled || loading}
        >
          {leadingElement}
          <div className={cn('bpm-btn-label', { loading })}>
            <span className='bpm-btn-label-content'>{children}</span>
            {loading && (
              <div className='bpm-btn-spinner'>
                <Spinner size={SPINNER_SIZE_BY_BUTTON_SIZE[size]} color='currentColor' />
              </div>
            )}
          </div>
          {trailingElement}
        </button>
      </TooltipWrapper>
      {interactiveTrailingElement}
    </>
  );
};

const TooltipWrapper = ({
  children,
  tooltip
}: {
  children: ReactElement;
  tooltip?: ButtonTooltip;
}) => {
  if (!tooltip) {
    return children;
  }

  const { text, placement = Placement.AUTO, class: tooltipClass } = tooltip;

  return (
    <OverlayTrigger
      placement={placement}
      overlay={
        <Tooltip id={`id:${text}`} className={cn('btn-tooltip', tooltipClass)}>
          {text}
        </Tooltip>
      }
    >
      {children}
    </OverlayTrigger>
  );
};
